import {
  CompleteTargets,
  getAlertSources,
  getDssLabelPerSource
} from '@weenat/client/dist/core/alerts'
import useGetCompleteAlertTargets from '@weenat/client/dist/core/alerts/useGetCompleteAlertTargets'
import useItemName from '@weenat/client/dist/core/utils/useItemName'
import { Alert, Source, isDssSource } from '@weenat/client/dist/resources/alerts.types'
import { WeenatColor } from '@weenat/theme'
import { useIntl } from '@weenat/wintl'
import Badge from 'app/src/kit/Badge'
import Icons from 'app/src/kit/Icons'
import Text from 'app/src/kit/Text'
import { capitalize, isEmpty, isNil } from 'lodash-es'
import { useMemo } from 'react'
import { styled } from 'styled-components'
import Link from '../kit/LinkComponent'
import LoadingList from '../kit/LoadingList'
import { Tooltip, TooltipContent, TooltipTrigger } from '../kit/tooltips/Tooltip'
import AlertActiveStateToggle from './AlertActiveStateToggle'

const TARGET_TO_DISPLAY_COUNT = 2

const AlertListItemCard = styled(Flex)`
  flex: 1;
  padding: ${(p) => p.theme.spacings.lg}px;
  border-color: ${(p) => p.theme.colors.grayscale[200]};
  border-width: 0;
  border-bottom-width: 1px;
  border-style: solid;
  justify-content: space-between;

  &:hover {
    background-color: ${(p) => p.theme.colors.primary[200]};
  }
`

interface AlertDetailsBadgeProps {
  bgColor?: WeenatColor
  color?: WeenatColor
  iconName: keyof typeof Icons
  text: string
}

const AlertDetailsBadge: FC<AlertDetailsBadgeProps> = ({
  bgColor = 'grayscale.900',
  color = 'grayscale.white',
  text,
  iconName
}) => {
  const Icn = Icons[iconName]
  return (
    <Badge
      $gap='sm'
      $p='sm'
      $px='sm'
      $color={color}
      $borderRadius={'md'}
      $backgroundColor={bgColor}
    >
      <Icn $color={color} $size='sm' />
      <Text $fontSize='sm' $fontWeight='regular' $color={color} style={{ marginRight: 2 }}>
        {text}
      </Text>
    </Badge>
  )
}

const TargetsList: FC<{ targets: CompleteTargets[] }> = ({ targets }) => {
  const { t } = useIntl()
  const firstTargetName = useItemName(targets[0])
  const label =
    targets.length === 1
      ? firstTargetName
      : t('alerts.targets_count', { nbOfTargets: targets.length })

  return (
    <Flex $alignItems='center'>
      {isEmpty(targets) ? (
        <Text $fontSize='sm' $color={'data.danger'}>
          {t('alerts.no_targets_abbr')}
        </Text>
      ) : (
        <Text $fontSize='sm' $color={'grayscale.700'}>
          {label}
        </Text>
      )}
    </Flex>
  )
}

const SourcesList: FC<{ sources: Source[] }> = ({ sources }) => {
  const { t } = useIntl()
  const displayed = sources.slice(0, 2)
  const moreCount = sources.length - displayed.length
  const label = displayed
    .map((s) => {
      return isDssSource(s) ? getDssLabelPerSource(s) : t(`metrics.${s}`, { capitalize: true })
    })
    .join(', ')

  return (
    <Flex $alignItems='center'>
      <Text $fontSize='sm' $color={'grayscale.700'} $ellipsis>
        {label}
      </Text>
      {moreCount !== 0 && <Text $fontSize='sm' $color='grayscale.700'>{`, + ${moreCount}`}</Text>}
    </Flex>
  )
}

const AlertListItem: FC<{
  alert: Alert
}> = ({ alert }) => {
  const { t } = useIntl()
  const { formatRelativeTimeToNow } = useIntl()

  const sources = useMemo(() => getAlertSources(alert), [alert])

  const completeOnlyOnFirstTwo = Object.entries(alert.targets).reduce(
    (acc, [key, value]) => ({ ...acc, [key]: value.slice(0, TARGET_TO_DISPLAY_COUNT) }),
    {} as Alert['targets']
  )

  const { completeTargets, isLoading } = useGetCompleteAlertTargets(
    completeOnlyOnFirstTwo,
    sources,
    false
  )
  const { isVoiceCallEnabled, isActive, latestTriggerTimestamp } = alert
  const isTriggered = alert.currentlyTriggerCount > 0
  const isRepeatable = Object.values(alert.predicates).some((predicate) =>
    'isRepeatable' in predicate.params ? predicate.params.isRepeatable : false
  )

  return (
    <>
      {isLoading ? (
        <LoadingList variant='single-line' size={1} height={64} uniqueKey={alert._id} />
      ) : (
        <Link to={`/alerts/${alert._id}`} $withoutHoverEffect>
          <AlertListItemCard>
            <Flex $flexDirection='column'>
              <Text $fontSize='md' $fontWeight='bold'>
                {alert.name}
              </Text>
              <Flex $gap='sm'>
                <SourcesList sources={sources} />
                <Text $fontWeight='bold' $color={'grayscale.700'}>
                  {'·'}
                </Text>
                <TargetsList targets={completeTargets} />
              </Flex>
            </Flex>
            <Flex $alignItems='center' $gap='md'>
              {isRepeatable && isActive && (
                <AlertDetailsBadge
                  text={t('alerts.paramSetups.fieldLabels.repeatable_abbrev', {
                    capitalize: true
                  })}
                  iconName='Refresh'
                />
              )}

              {isVoiceCallEnabled && isActive && (
                <>
                  <Tooltip>
                    <TooltipTrigger>
                      <AlertDetailsBadge
                        text={t('alerts.voice_call_title', {
                          capitalize: true
                        })}
                        iconName='PhoneInTalk'
                      />
                    </TooltipTrigger>
                    <TooltipContent style={{ color: 'white' }}>
                      <Flex $gap='sm'>
                        <Icons.InfoCircle $color={'grayscale.white'} $size='md' />
                        <Text>{t('user_settings.phone_numbers.add_weenat_phone_to_contacts')}</Text>
                        <Text $fontWeight='bold'>{t('weenat.support.alert_phoneNumber')}</Text>
                      </Flex>
                    </TooltipContent>
                  </Tooltip>
                </>
              )}
              {isTriggered && isActive && !isNil(latestTriggerTimestamp) && (
                <AlertDetailsBadge
                  bgColor={'data.danger'}
                  text={capitalize(
                    formatRelativeTimeToNow(latestTriggerTimestamp, { style: 'long' })
                  )}
                  iconName='ClockFilled'
                />
              )}
              <AlertActiveStateToggle id={alert._id} isActive={alert.isActive} withLabel={false} />
            </Flex>
          </AlertListItemCard>
        </Link>
      )}
    </>
  )
}

export default AlertListItem
